import React, { useId } from 'react';
import {
  SPEED_BANDS,
  formatClock,
  formatPercent,
  formatSpeed,
  speedBuckets,
  type JoystickSession,
} from '../../data/joystick-mock';

function niceMax(value: number): number {
  const safe = Math.max(value, 1);
  const pow = 10 ** Math.floor(Math.log10(safe));
  const scaled = safe / pow;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return step * pow;
}

function SpeedChart({ session }: { session: JoystickSession }) {
  const uid = useId().replace(/:/g, '');
  const width = 520;
  const height = 210;
  const pad = { l: 44, r: 16, t: 28, b: 28 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const series = session.speedSeries.length > 0 ? session.speedSeries : [{ t: 0, speed: 0 }, { t: session.durationSec, speed: 0 }];
  const maxT = Math.max(session.durationSec, ...series.map((sample) => sample.t), 1);
  const yMax = niceMax(Math.max(session.maxSpeedKmh, ...series.map((sample) => sample.speed)));
  const xAt = (t: number) => pad.l + (t / maxT) * innerW;
  const yAt = (speed: number) => pad.t + innerH - (speed / yMax) * innerH;
  const line = series
    .map((sample, index) => `${index === 0 ? 'M' : 'L'} ${xAt(sample.t).toFixed(1)} ${yAt(sample.speed).toFixed(1)}`)
    .join(' ');
  const area = `${line} L ${xAt(series[series.length - 1].t).toFixed(1)} ${yAt(0).toFixed(1)} L ${xAt(series[0].t).toFixed(1)} ${yAt(0).toFixed(1)} Z`;
  const peak = series.reduce((best, sample) => (sample.speed > best.speed ? sample : best), series[0]);
  const ticks = [0, 0.5, 1].map((part) => ({ x: xAt(maxT * part), label: (maxT * part / 60).toFixed(2) }));
  const yTicks = [0, 0.33, 0.66, 1].map((part) => ({ y: yAt(yMax * part), label: Math.round(yMax * part).toString() }));

  return (
    <section className="js-panel" data-testid="joystick-speed">
      <header className="js-panel__head">
        <h3>Speed Over Time</h3>
        <p>{`Avg: ${formatSpeed(session.avgSpeedKmh)}  |  Max: ${formatSpeed(session.maxSpeedKmh)}`}</p>
      </header>
      <svg className="js-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Speed over time">
        <defs>
          <linearGradient id={`${uid}-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--ml-primary, #7c5cff)" stopOpacity="0.45" />
            <stop offset="1" stopColor="var(--ml-primary, #7c5cff)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {yTicks.map((tick) => (
          <g key={tick.label}>
            <line className="js-chart__grid" x1={pad.l} x2={width - pad.r} y1={tick.y} y2={tick.y} />
            <text className="js-chart__tick" x={pad.l - 8} y={tick.y + 4} textAnchor="end">{tick.label}</text>
          </g>
        ))}
        <path d={area} fill={`url(#${uid}-fill)`} />
        <path d={line} className="js-chart__line" />
        <g transform={`translate(${xAt(peak.t)} ${Math.max(18, yAt(peak.speed) - 18)})`}>
          <rect className="js-chart__pill" x="-46" y="-14" width="92" height="22" rx="11" />
          <text className="js-chart__pill-text" y="2" textAnchor="middle">{formatSpeed(peak.speed)}</text>
        </g>
        {ticks.map((tick) => (
          <text key={tick.label} className="js-chart__tick" x={tick.x} y={height - 8} textAnchor="middle">{tick.label}</text>
        ))}
      </svg>
    </section>
  );
}

function ElevationChart({ session }: { session: JoystickSession }) {
  const uid = useId().replace(/:/g, '');
  const width = 520;
  const height = 210;
  const pad = { l: 44, r: 16, t: 28, b: 28 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const series = session.elevationSeries.length > 0
    ? session.elevationSeries
    : [{ km: 0, meters: session.minElevationM }, { km: session.distanceKm, meters: session.maxElevationM }];
  const maxKm = Math.max(session.distanceKm, ...series.map((sample) => sample.km), 0.1);
  const span = Math.max(8, session.maxElevationM - session.minElevationM);
  const y0 = session.minElevationM - span * 0.35;
  const y1 = session.maxElevationM + span * 0.45;
  const xAt = (km: number) => pad.l + (km / maxKm) * innerW;
  const yAt = (meters: number) => pad.t + innerH - ((meters - y0) / (y1 - y0)) * innerH;
  const line = series
    .map((sample, index) => `${index === 0 ? 'M' : 'L'} ${xAt(sample.km).toFixed(1)} ${yAt(sample.meters).toFixed(1)}`)
    .join(' ');
  const area = `${line} L ${xAt(series[series.length - 1].km).toFixed(1)} ${yAt(y0).toFixed(1)} L ${xAt(series[0].km).toFixed(1)} ${yAt(y0).toFixed(1)} Z`;
  const last = series[series.length - 1];
  const yTicks = [0, 0.5, 1].map((part) => {
    const meters = y0 + (y1 - y0) * part;
    return { y: yAt(meters), label: Math.round(meters).toString() };
  });
  const xTicks = [0, 0.5, 1].map((part) => ({
    x: xAt(maxKm * part),
    label: `${(maxKm * part).toFixed(1)} km`,
  }));

  return (
    <section className="js-panel" data-testid="joystick-elevation">
      <header className="js-panel__head">
        <h3>Elevation Over Distance</h3>
        <p>{`Min: ${session.minElevationM} m  |  Max: ${session.maxElevationM} m`}</p>
      </header>
      <svg className="js-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Elevation over distance">
        <defs>
          <linearGradient id={`${uid}-elev`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--ml-info, #3b82f6)" stopOpacity="0.35" />
            <stop offset="1" stopColor="var(--ml-info, #3b82f6)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {yTicks.map((tick) => (
          <g key={tick.label}>
            <line className="js-chart__grid" x1={pad.l} x2={width - pad.r} y1={tick.y} y2={tick.y} />
            <text className="js-chart__tick" x={pad.l - 8} y={tick.y + 4} textAnchor="end">{tick.label}</text>
          </g>
        ))}
        <path d={area} fill={`url(#${uid}-elev)`} />
        <path d={line} className="js-chart__line js-chart__line--blue" />
        <g transform={`translate(${Math.min(width - 58, xAt(last.km))} ${Math.max(18, yAt(last.meters) - 16)})`}>
          <rect className="js-chart__pill js-chart__pill--blue" x="-36" y="-14" width="52" height="22" rx="11" />
          <text className="js-chart__pill-text" x="-10" y="2" textAnchor="middle">{`${last.meters} m`}</text>
        </g>
        {xTicks.map((tick) => (
          <text key={tick.label} className="js-chart__tick" x={tick.x} y={height - 8} textAnchor="middle">{tick.label}</text>
        ))}
      </svg>
    </section>
  );
}

function DistributionChart({ session }: { session: JoystickSession }) {
  const values = session.buckets ?? speedBuckets(session.speedSeries);
  const width = 520;
  const height = 200;
  const pad = { l: 36, r: 10, t: 16, b: 32 };
  const innerW = width - pad.l - pad.r;
  const innerH = height - pad.t - pad.b;
  const peak = Math.max(5, ...values);
  const yMax = Math.ceil(peak / 5) * 5;
  const slot = innerW / values.length;

  return (
    <section className="js-panel" data-testid="joystick-distribution">
      <header className="js-panel__head">
        <h3>Speed Distribution</h3>
      </header>
      <svg className="js-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Speed distribution">
        {[0, 0.5, 1].map((part) => {
          const y = pad.t + innerH - part * innerH;
          return (
            <g key={part}>
              <line className="js-chart__grid" x1={pad.l} x2={width - pad.r} y1={y} y2={y} />
              <text className="js-chart__tick" x={pad.l - 8} y={y + 4} textAnchor="end">{Math.round(yMax * part)}</text>
            </g>
          );
        })}
        {values.map((value, index) => {
          const barH = (value / yMax) * innerH;
          const x = pad.l + index * slot + slot * 0.22;
          const barW = slot * 0.56;
          const y = pad.t + innerH - barH;
          return (
            <g key={SPEED_BANDS[index]}>
              <rect className="js-chart__bar" x={x} y={y} width={barW} height={Math.max(barH, value > 0 ? 4 : 0)} rx="6" />
              <text className="js-chart__tick" x={x + barW / 2} y={height - 10} textAnchor="middle">{SPEED_BANDS[index]}</text>
            </g>
          );
        })}
      </svg>
    </section>
  );
}

function MovementDonut({ session }: { session: JoystickSession }) {
  const total = session.durationSec || 1;
  const pct = Math.round((session.movingSec / total) * 100);
  const radius = 42;
  const circ = 2 * Math.PI * radius;
  const dash = (Math.min(100, Math.max(0, pct)) / 100) * circ;

  return (
    <section className="js-panel" data-testid="joystick-movement">
      <header className="js-panel__head">
        <h3>Movement Summary</h3>
      </header>
      <div className="js-move">
        <svg className="js-donut" viewBox="0 0 120 120" role="img" aria-label={`${pct} percent moving`}>
          <circle className="js-donut__track" cx="60" cy="60" r={radius} />
          <circle
            className="js-donut__val"
            cx="60"
            cy="60"
            r={radius}
            strokeDasharray={pct >= 100 ? undefined : `${dash} ${circ}`}
            transform="rotate(-90 60 60)"
          />
          <text className="js-donut__pct" x="60" y="56" textAnchor="middle">{`${pct}%`}</text>
          <text className="js-donut__cap" x="60" y="74" textAnchor="middle">Moving</text>
        </svg>
        <ul className="js-legend">
          <li>
            <i className="js-legend__swatch js-legend__swatch--move" />
            <span>
              <b>Moving</b>
              {`${formatClock(session.movingSec)} (${formatPercent(session.movingSec, total)})`}
            </span>
          </li>
          <li>
            <i className="js-legend__swatch js-legend__swatch--still" />
            <span>
              <b>Stationary Time</b>
              {`${formatClock(session.stationarySec)} (${formatPercent(session.stationarySec, total)})`}
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
}

export function StatsPanels({ session }: { session: JoystickSession }) {
  return (
    <div className="js-stats">
      <div className="js-stats__charts">
        <SpeedChart session={session} />
        <ElevationChart session={session} />
      </div>
      <div className="js-stats__lower">
        <DistributionChart session={session} />
        <MovementDonut session={session} />
      </div>
    </div>
  );
}

export default StatsPanels;
